import { defineStore } from "pinia";

/**
 * Entity types that support revision tracking and conflict detection
 */
export type TrackableEntityType = "observation" | "direct_work";

/**
 * State for a tracked document
 */
interface DocumentState {
  /** The latest version known from the server (via SSE) */
  remoteVersion: number;
  /** The version the user loaded/last saved locally */
  localVersion: number;
  /** Information about who last updated the document */
  updatedBy: { id: number; name: string } | null;
  /** Whether the document has been deleted by another user */
  isDeleted: boolean;
}

/**
 * Entity Conflict Store
 *
 * Tracks document versions for conflict detection when multiple users
 * edit the same document simultaneously. Works with the Transmit SSE
 * infrastructure to receive real-time notifications.
 *
 * Usage:
 * 1. On page load, call setLocalVersion() with the document's current version
 * 2. Subscribe to the entity channel via transmitStore.subscribeEntity()
 * 3. When SSE messages arrive, the handler calls setRemoteVersion()
 * 4. Before saving, check hasConflict() to warn the user
 * 5. After successful save, call setLocalVersion() with the new version
 * 6. On unmount, call clear() and unsubscribeEntity()
 */
export const useEntityConflictStore = defineStore("entityConflict", () => {
  const documents = ref(new Map<string, DocumentState>());

  /**
   * Generate a unique key for storing document state
   */
  function getKey(entityType: string, entityId: number): string {
    return `${entityType}:${entityId}`;
  }

  /**
   * Set the remote version (called when SSE message received)
   * This indicates another user has updated the document
   */
  function setRemoteVersion(
    entityType: TrackableEntityType,
    entityId: number,
    version: number,
    updatedBy: { id: number; name: string },
  ): void {
    const key = getKey(entityType, entityId);
    const existing = documents.value.get(key);

    documents.value.set(key, {
      remoteVersion: version,
      localVersion: existing?.localVersion ?? version,
      updatedBy,
      isDeleted: false,
    });
  }

  /**
   * Set the local version (called on page load and after successful save)
   */
  function setLocalVersion(
    entityType: TrackableEntityType,
    entityId: number,
    version: number,
  ): void {
    const key = getKey(entityType, entityId);
    const existing = documents.value.get(key);

    documents.value.set(key, {
      remoteVersion: existing?.remoteVersion ?? version,
      localVersion: version,
      updatedBy: existing?.updatedBy ?? null,
      isDeleted: existing?.isDeleted ?? false,
    });
  }

  /**
   * Mark a document as deleted (called when SSE delete message received)
   */
  function markDeleted(entityType: TrackableEntityType, entityId: number): void {
    const key = getKey(entityType, entityId);
    const existing = documents.value.get(key);

    if (existing) {
      documents.value.set(key, { ...existing, isDeleted: true });
    } else {
      // Create a minimal deleted state
      documents.value.set(key, {
        remoteVersion: 0,
        localVersion: 0,
        updatedBy: null,
        isDeleted: true,
      });
    }
  }

  /**
   * Check if there's a conflict (remote version > local version)
   * This means another user has saved changes since we loaded the document
   */
  function hasConflict(entityType: TrackableEntityType, entityId: number): boolean {
    const key = getKey(entityType, entityId);
    const doc = documents.value.get(key);

    if (!doc) return false;
    return doc.remoteVersion > doc.localVersion;
  }

  /**
   * Check if the document has been deleted by another user
   */
  function isDeleted(entityType: TrackableEntityType, entityId: number): boolean {
    const key = getKey(entityType, entityId);
    const doc = documents.value.get(key);
    return doc?.isDeleted ?? false;
  }

  /**
   * Get the current remote version
   */
  function getRemoteVersion(entityType: TrackableEntityType, entityId: number): number | null {
    const key = getKey(entityType, entityId);
    return documents.value.get(key)?.remoteVersion ?? null;
  }

  /**
   * Get information about who last updated the document
   */
  function getUpdatedBy(
    entityType: TrackableEntityType,
    entityId: number,
  ): { id: number; name: string } | null {
    const key = getKey(entityType, entityId);
    return documents.value.get(key)?.updatedBy ?? null;
  }

  /**
   * Clear tracking for a document (call when leaving edit page)
   */
  function clear(entityType: TrackableEntityType, entityId: number): void {
    const key = getKey(entityType, entityId);
    documents.value.delete(key);
  }

  /**
   * Refresh the document by reloading the page
   * This ensures we get the latest data from the server
   */
  function refreshDocument(entityType: TrackableEntityType, entityId: number): void {
    // Force refresh by reloading the page
    window.location.reload();
  }

  /**
   * Clear all tracked documents (useful for logout)
   */
  function clearAll(): void {
    documents.value.clear();
  }

  return {
    documents: readonly(documents),
    setRemoteVersion,
    setLocalVersion,
    markDeleted,
    hasConflict,
    isDeleted,
    getRemoteVersion,
    getUpdatedBy,
    clear,
    refreshDocument,
    clearAll,
  };
});
